import React from 'react';
import { ButtonLink, InlineLink, ResourceLink } from '../shared/AcademicLink';

const MAChristianEducation = () => {
  const coreCourses = [
    { code: 'CE 500', name: 'Introduction to Christian Education', credits: 3 },
    { code: 'CE 501', name: 'Educational Psychology', credits: 3 },
    { code: 'CE 503', name: 'Philosophy and Principles of Christian Education', credits: 3 },
    { code: 'CE 509', name: 'Children\'s Ministry in the Growing Church', credits: 3 },
    { code: 'CE 511', name: 'Family Life Ministry', credits: 3 },
    { code: 'CE 515', name: 'Developing a Total Church Education Program', credits: 3 },
    { code: 'CE 519', name: 'Christian History and Heritage', credits: 3 },
    { code: 'CE 521', name: 'Program Development, Implementation and Evaluation', credits: 3 },
    { code: 'CE 530', name: 'Developing Lay Leaders and Lay Pastors in the Church', credits: 3 },
    { code: 'CE 533', name: 'Church Staff and Members Relations Practicum', credits: 3 }
  ];

  const concentrations = [
    { name: 'Adult Ministry', icon: '👥' },
    { name: 'Business Ministry', icon: '💼' },
    { name: 'Children\'s Ministry', icon: '🧒' },
    { name: 'Collegiate Ministry', icon: '🎓' },
    { name: 'Counseling Ministry', icon: '🤝' },
    { name: 'General Ministry', icon: '🌟' },
    { name: 'Student Ministry', icon: '📚' },
    { name: 'Youth Ministry', icon: '🌱' },
    { name: 'Worship Ministry', icon: '🎵' }
  ];

  const programFeatures = [
    {
      title: "Educational Excellence",
      description: "Comprehensive training in Christian education principles and practices",
      icon: "📚"
    },
    {
      title: "Practical Application",
      description: "Hands-on experience through practicum and field work",
      icon: "✋"
    },
    {
      title: "Leadership Development",
      description: "Training in program development and team leadership",
      icon: "👥"
    },
    {
      title: "Ministry Integration",
      description: "Integration of educational theory with ministry practice",
      icon: "🔄"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl font-bold mb-4">
              Master of Arts in Christian Education
            </h1>
            <p className="text-xl opacity-90">
              Advanced training in educational ministry and leadership
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Overview Section */}
          <section className="mb-16">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-2xl font-bold mb-6">Program Overview</h2>
              <p className="text-gray-600 mb-4">
                The Master of Arts in Christian Education consists of 36 semester hours 
                of coursework. Students take 30 hours of core subjects and 6 hours in 
                a concentration to prepare them for various ministry roles.
              </p>
            </div>
          </section>

          {/* Program Features */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Program Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {programFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-primary-500"></div>
                  <div className="flex items-start gap-4">
                    <span className="text-3xl">{feature.icon}</span>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-primary-600">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Core Courses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Core Courses (30 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="divide-y divide-gray-200">
                {coreCourses.map((course, index) => (
                  <div key={index} className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {course.code}: {course.name}
                        </h3>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                        {course.credits} Credits
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Concentrations */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Concentrations (6 credits)</h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-600 mb-6">
                The concentration will be a practicum in a Christian education field under 
                the supervision of a college professor and a working Christian education 
                professional.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                {concentrations.map((concentration, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
                    <span className="text-2xl">{concentration.icon}</span>
                    <span className="text-gray-700">{concentration.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Begin Your Educational Ministry Journey</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Join our community of educational leaders and make an impact in Christian education.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <ButtonLink to="/application" primary>
                  Apply Now
                </ButtonLink>
                <ButtonLink to="/request" primary={false}>
                  Request Information
                </ButtonLink>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MAChristianEducation; 